import { useContext } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { SystemInputs } from "@graylog/server-api";
import fetch from "logic/rest/FetchProvider";
import { qualifyUrl } from "util/URLUtils";
import UserNotification from "util/UserNotification";
import InputsContext from "contexts/InputsContext";
const SOURCE_URL = "/system/inputs";
const INPUTS_QUERY_KEY = ["inputs", "list"];
export const fetchInputs = () => fetch("GET", qualifyUrl(SOURCE_URL));
export const fetchInput = (inputId) => fetch("GET", qualifyUrl(`${SOURCE_URL}/${inputId}`));
export const createInput = (input) => {
  const promise = fetch("POST", qualifyUrl(`${SOURCE_URL}?setup_wizard=true`), input);
  promise.then(
    () => {
      UserNotification.success(`Input '${input.title}' launched successfully`);
    },
    (error) => {
      UserNotification.error(`Launching input '${input.title}' failed with status: ${error}`, "Could not launch input");
    }
  );
  return promise;
};
export const updateInput = (id, input) => {
  const promise = fetch("PUT", qualifyUrl(`${SOURCE_URL}/${id}`), input);
  promise.then(
    () => {
      UserNotification.success(`Input '${input.title}' updated successfully`);
    },
    (error) => {
      UserNotification.error(`Updating input '${input.title}' failed with status: ${error}`, "Could not update input");
    }
  );
  return promise;
};
export const deleteInput = (input) => {
  const promise = SystemInputs.terminate(input.id);
  promise.then(
    () => {
      UserNotification.success(`Input '${input.title}' deleted successfully`);
    },
    (error) => {
      UserNotification.error(`Deleting input '${input.title}' failed with status: ${error}`, "Could not delete input");
    }
  );
  return promise;
};
export const inputsAsMap = (inputsList) => {
  const inputsMap = {};
  inputsList.forEach((input) => {
    inputsMap[input.id] = input;
  });
  return inputsMap;
};
const useInputsList = () => useQuery({
  queryKey: INPUTS_QUERY_KEY,
  queryFn: () => fetchInputs().catch((error) => {
    UserNotification.error(`Fetching Inputs failed with status: ${error}`, "Could not retrieve Inputs");
    throw error;
  }),
  select: (data) => data.inputs
});
export const useCreateInput = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input) => createInput(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: INPUTS_QUERY_KEY });
    }
  });
};
export const useInputs = () => useContext(InputsContext);
export { INPUTS_QUERY_KEY };
export default useInputsList;
